import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Play, Calendar, Eye, MoreVertical, Search, Bell } from 'lucide-react';
import { format } from 'date-fns';

// Demo data for screenshots - matching Flutter app
const DEMO_STREAMS = [
  {
    id: 'demo-001',
    title: 'Sunday Service - Living a Purpose-Driven Life',
    description: 'Join us for a powerful message on discovering and living out your God-given purpose.',
    thumbnail_url: 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=800&q=80',
    status: 'live',
    scheduled_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    viewer_count: 234,
    host: { name: 'Pastor David Ogbueli', initials: 'PD' },
    tags: ['Sunday Service', 'Worship', 'Teaching'],
  },
  {
    id: 'demo-002',
    title: 'Wednesday Bible Study - Book of Acts',
    description: 'Deep dive into the Book of Acts as we explore the early church.',
    thumbnail_url: 'https://images.unsplash.com/photo-1507692049790-de58293a4697?w=800&q=80',
    status: 'scheduled',
    scheduled_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    viewer_count: 0,
    host: { name: 'Pastor Sarah Johnson', initials: 'PS' },
    tags: ['Bible Study', 'Midweek'],
  },
  {
    id: 'demo-003',
    title: 'Youth Explosion - Ignite Your Potential',
    description: 'An explosive encounter for young people ready to ignite their potential.',
    thumbnail_url: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800&q=80',
    status: 'scheduled',
    scheduled_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    viewer_count: 0,
    host: { name: 'Evangelist Mike Brown', initials: 'EM' },
    tags: ['Youth', 'Worship'],
  },
  {
    id: 'demo-004',
    title: 'Leadership Summit 2024',
    description: 'Annual leadership summit equipping pastors and ministry leaders.',
    thumbnail_url: 'https://images.unsplash.com/photo-1544531586-fde5298cdd40?w=800&q=80',
    status: 'ended',
    scheduled_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    viewer_count: 567,
    host: { name: 'Pastor David Ogbueli', initials: 'PD' },
    tags: ['Leadership', 'Training'],
  },
  {
    id: 'demo-005',
    title: 'Worship Night - Encounter His Presence',
    description: 'An intimate night of worship and adoration.',
    thumbnail_url: 'https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=800&q=80',
    status: 'ended',
    scheduled_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    viewer_count: 423,
    host: { name: 'Worship Leader Grace Williams', initials: 'GW' },
    tags: ['Worship', 'Night Service'],
  },
];

// Filter chips data
const FILTER_CHIPS = [
  { id: 'all', label: 'All', icon: null },
  { id: 'live', label: 'Live', icon: 'dot', live: true },
  { id: 'upcoming', label: 'Upcoming', icon: 'calendar' },
  { id: 'ended', label: 'Ended', icon: 'check' },
];

export default function DemoStreamsPage() {
  const [viewerCount, setViewerCount] = useState(234);
  const [activeFilter, setActiveFilter] = useState('all');

  // Animate viewer count for live stream
  useEffect(() => {
    const interval = setInterval(() => {
      setViewerCount(prev => prev + Math.floor(Math.random() * 10) - 5);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#000000] text-[#F1F5F9]">
      {/* Demo Notice Banner */}
      <div className="bg-primary/20 border-b border-primary px-4 py-3 text-center">
        <p className="text-sm text-primary font-medium">
          📸 Demo Mode - Showing sample content for screenshots
        </p>
      </div>

      {/* Header - Matching Flutter AppBar */}
      <header className="sticky top-0 z-50 w-full border-b border-[#2A2A2A] bg-[#000000]/95 backdrop-blur supports-[backdrop-filter]:bg-[#000000]/60">
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">DM</span>
            </div>
            <span className="text-xl font-bold">Dominion Mandate</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="text-[#F1F5F9]">
              <Search className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" className="text-[#F1F5F9]">
              <Bell className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center text-white font-semibold">
              JD
            </div>
          </div>
        </div>
      </header>

      {/* Filter Chips - Matching Flutter */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto border-b border-[#2A2A2A] bg-[#000000]">
        {FILTER_CHIPS.map((chip) => (
          <button
            key={chip.id}
            onClick={() => setActiveFilter(chip.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium whitespace-nowrap transition-colors ${
              activeFilter === chip.id
                ? 'bg-primary border-primary text-white'
                : 'bg-[#0A0A0A] border-[#2A2A2A] text-[#94A3B8] hover:border-[#333]'
            }`}
          >
            {chip.icon === 'dot' && (
              <span className="w-2 h-2 rounded-full bg-current" />
            )}
            {chip.icon === 'calendar' && <Calendar className="w-3.5 h-3.5" />}
            {chip.icon === 'check' && <span className="text-xs">✓</span>}
            {chip.label}
          </button>
        ))}
      </div>

      <main className="p-4">
        {/* Live Now Section */}
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-red-500 hover:bg-red-600 text-xs px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse mr-1.5" />
              LIVE
            </Badge>
            <h2 className="text-xl font-bold">Watch Now</h2>
          </div>

          {/* Live Stream Card - YouTube Style */}
          {DEMO_STREAMS.filter(s => s.status === 'live').map((stream) => (
            <Card key={stream.id} className="overflow-hidden bg-transparent border-0 mb-4">
              <div className="relative aspect-video">
                <img
                  src={stream.thumbnail_url}
                  alt={stream.title}
                  className="w-full h-full object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/80 rounded-xl" />
                
                {/* Live Badge */}
                <div className="absolute top-2 left-2">
                  <Badge className="bg-red-500 hover:bg-red-600 text-xs">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse mr-1" />
                    LIVE
                  </Badge>
                </div>

                {/* Viewer Count */}
                <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm rounded px-2 py-1 flex items-center gap-1 text-xs">
                  <Eye className="w-3 h-3" />
                  <span>{viewerCount}</span>
                </div>
              </div>
              
              <CardContent className="p-0 pt-3">
                <div className="flex gap-3">
                  {/* Host Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    {stream.host.initials}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base line-clamp-2 mb-1">
                      {stream.title}
                    </h3>
                    <p className="text-sm text-[#94A3B8]">{stream.host.name}</p>
                    <p className="text-xs text-[#64748B]">Started 30 min ago</p>
                  </div>
                  
                  <Button variant="ghost" size="icon" className="text-[#94A3B8]">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Tags */}
                <div className="flex gap-2 mt-2 ml-13">
                  {stream.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs bg-primary/20 text-primary hover:bg-primary/30">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* Upcoming Streams Section */}
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-4">Upcoming Streams</h2>

          {DEMO_STREAMS.filter(s => s.status === 'scheduled').map((stream) => (
            <Card key={stream.id} className="overflow-hidden bg-transparent border-0 mb-4">
              <div className="relative aspect-video">
                <img
                  src={stream.thumbnail_url}
                  alt={stream.title}
                  className="w-full h-full object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/80 rounded-xl" />
                
                {/* Upcoming Badge */}
                <div className="absolute top-2 left-2">
                  <Badge className="bg-primary text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    UPCOMING
                  </Badge>
                </div>

                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-black/80 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                  </div>
                </div>
              </div>
              
              <CardContent className="p-0 pt-3">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    {stream.host.initials}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base line-clamp-2 mb-1">
                      {stream.title}
                    </h3>
                    <p className="text-sm text-[#94A3B8]">{stream.host.name}</p>
                    <p className="text-xs text-[#64748B]">{format(new Date(stream.scheduled_at), 'EEE, h:mm a')}</p>
                  </div>
                  
                  <Button variant="ghost" size="icon" className="text-[#94A3B8]">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Tags */}
                <div className="flex gap-2 mt-2 ml-13">
                  {stream.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs bg-primary/20 text-primary hover:bg-primary/30">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* Recent Streams Section */}
        <section>
          <h2 className="text-xl font-bold mb-4">Recent Streams</h2>

          {DEMO_STREAMS.filter(s => s.status === 'ended').map((stream) => (
            <Card key={stream.id} className="overflow-hidden bg-transparent border-0 mb-4">
              <div className="relative aspect-video">
                <img
                  src={stream.thumbnail_url}
                  alt={stream.title}
                  className="w-full h-full object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/80 rounded-xl" />
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-black/80 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                  </div>
                </div>
              </div>
              
              <CardContent className="p-0 pt-3">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    {stream.host.initials}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base line-clamp-2 mb-1">
                      {stream.title}
                    </h3>
                    <p className="text-sm text-[#94A3B8]">{stream.host.name}</p>
                    <p className="text-xs text-[#64748B]">
                      {format(new Date(stream.scheduled_at), 'MMM d, y')} • {stream.viewer_count} views
                    </p>
                  </div>
                  
                  <Button variant="ghost" size="icon" className="text-[#94A3B8]">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Tags */}
                <div className="flex gap-2 mt-2 ml-13">
                  {stream.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs bg-primary/20 text-primary hover:bg-primary/30">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      </main>

      {/* Bottom Navigation - Matching Flutter */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-[#2A2A2A] flex justify-around pb-safe">
        <a href="#" className="flex flex-col items-center gap-1 py-2 px-4 text-primary">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
          <span className="text-xs">Home</span>
        </a>
        <a href="#" className="flex flex-col items-center gap-1 py-2 px-4 text-[#94A3B8]">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
          </svg>
          <span className="text-xs">Streams</span>
        </a>
        <a href="#" className="flex flex-col items-center gap-1 py-2 px-4 text-[#94A3B8]">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
          </svg>
          <span className="text-xs">Media</span>
        </a>
        <a href="#" className="flex flex-col items-center gap-1 py-2 px-4 text-[#94A3B8]">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          <span className="text-xs">Profile</span>
        </a>
      </nav>
    </div>
  );
}
